import { alpha, Theme } from '@mui/material';
import { CSSObject } from '@mui/material/styles';

export const UPGRADE_BANNER_CLASS = 'upgrade-banner';
export const UPGRADE_BANNER_ICON_CLASS = 'upgrade-banner__icon';
export const UPGRADE_BANNER_TEXT_CLASS = 'upgrade-banner__text';

/**
 * Standing free-tier upgrade banner shown to constituents on the homepage.
 *
 * Registered against `MuiPaper` in both palettes so the two stay in step and the
 * component only decides *what* it says, not what it looks like. The glass treatment
 * every other Paper gets is deliberately switched off here: the banner is a system
 * message sitting above the dashboard, not another card competing with it.
 */
export const upgradeBannerStyles = (theme: Theme): CSSObject => {
	const accent = theme.palette.primary.main;

	return {
		[`&.${UPGRADE_BANNER_CLASS}`]: {
			alignItems: 'center',
			background: `linear-gradient(90deg, ${alpha(accent, 0.1)} 0%, ${alpha(accent, 0.02)} 100%)`,
			backdropFilter: 'none',
			WebkitBackdropFilter: 'none',
			border: `1px solid ${alpha(accent, 0.28)}`,
			borderRadius: 16,
			boxShadow: 'none',
			display: 'flex',
			gap: theme.spacing(2),
			marginBottom: theme.spacing(2.5),
			padding: theme.spacing(1.5, 2.5),

			[theme.breakpoints.down('sm')]: {
				alignItems: 'flex-start',
				flexDirection: 'column',
				gap: theme.spacing(1.5),
				padding: theme.spacing(1.75, 2)
			},

			[`& .${UPGRADE_BANNER_ICON_CLASS}`]: {
				alignItems: 'center',
				backgroundColor: alpha(accent, 0.12),
				borderRadius: '50%',
				color: accent,
				display: 'flex',
				flexShrink: 0,
				height: 40,
				justifyContent: 'center',
				width: 40
			},

			[`& .${UPGRADE_BANNER_TEXT_CLASS}`]: {
				flexGrow: 1,
				// Lets the description ellipsise rather than force the row wider.
				minWidth: 0,

				'& .MuiTypography-subtitle1': {
					fontSize: 15,
					fontWeight: 700,
					lineHeight: 1.3
				},
				'& .MuiTypography-body2': {
					color: theme.palette.text.secondary,
					lineHeight: 1.4
				}
			},

			'& .MuiButton-root': {
				borderRadius: 8,
				flexShrink: 0,
				paddingInline: theme.spacing(2.5),
				[theme.breakpoints.down('sm')]: {
					alignSelf: 'stretch'
				}
			}
		}
	};
};
